// Tier configuration for subscription management
// Defines limits, features, and access controls for each tier

// List of admin user IDs (UIDs from Firebase Auth) for dev tier
// To find your UID: 
// 1. Go to Firebase Console > Authentication > Users
// 2. Find your email and copy the "User UID" column
// Or run this in browser console when logged in: 
//   firebase.auth().currentUser.uid
export const DEV_UIDS = [
  // Add your Firebase UID here to get dev/admin access
  // Example: 'Abc123XYZ456defGHI789...',
  'QXyrabt2UhP2yY0oisYDKzaqagH3',  // Trevor Gorham (trevor.w.gorham@gmail.com)
  'ZdEKCooorJhKfA6nDN4MZh1q8482',  // Paul Langfield
  'xzxoZF9L0SNzyML1YzNo4ehLiS83',  // Russ Gorham
  'YRTeahcF7zQExNQqvq8vJBxAit63',  // Matthew Price
  'xUI06aV90dbpFoEq6eOkIJ6ZFEh1',  // Kestrel (AI assistant)
];

export const TIERS = {
  free: {
    id: 'free',
    name: 'Free',
    description: 'Get started with habit tracking',
    monthlyPrice: 0,
    yearlyPrice: 0,
    limits: {
      maxHabits: 5,
      maxGoals: 3,
      maxTasks: 5,
      maxEvents: 10,
    },
    features: {
      habits: true,
      basicStats: true,
      streaks: true,
      tasks: true,
      goals: true,
      milestones: false,
      events: true,
      googleCalendar: false,
      analytics: false,
      themes: true,
      exportData: false,
      recurringTasks: false,
      prioritySupport: false,
    },
    featureList: [
      { text: 'Up to 5 habits', included: true },
      { text: 'Up to 5 tasks', included: true },
      { text: 'Up to 3 goals', included: true },
      { text: 'Up to 10 events', included: true },
      { text: 'Daily tracking & streaks', included: true },
      { text: 'Basic statistics', included: true },
      { text: 'Custom color themes', included: true },
      { text: 'Google Calendar sync', included: false },
    ],
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    description: 'Unlock all features',
    monthlyPrice: 5,
    yearlyPrice: 50,
    limits: {
      maxHabits: Infinity,
      maxGoals: Infinity,
      maxTasks: Infinity,
      maxEvents: Infinity,
    },
    features: {
      habits: true,
      basicStats: true,
      streaks: true,
      tasks: true,
      goals: true,
      milestones: true,
      events: true,
      googleCalendar: true,
      analytics: true,
      themes: true,
      exportData: true,
      recurringTasks: true,
      prioritySupport: true,
    },
    featureList: [
      { text: 'Everything in Free', included: true },
      { text: 'Unlimited habits, tasks & goals', included: true },
      { text: 'Task management', included: true },
      { text: 'Goal setting & milestones', included: true },
      { text: 'Calendar & events', included: true },
      { text: 'Google Calendar sync', included: true },
      { text: 'Custom color themes', included: true },
      { text: 'Advanced analytics', included: true },
      { text: 'Priority support', included: true },
    ],
  },
  friend: {
    id: 'friend',
    name: 'Friend of mind.',
    description: 'Unlimited access for friends & family',
    monthlyPrice: 0,
    yearlyPrice: 0,
    limits: {
      maxHabits: Infinity,
      maxGoals: Infinity,
      maxTasks: Infinity,
      maxEvents: Infinity,
    },
    features: {
      habits: true,
      basicStats: true,
      streaks: true,
      tasks: true,
      goals: true,
      milestones: true,
      events: true,
      googleCalendar: true,
      analytics: true,
      themes: true,
      exportData: true,
      recurringTasks: true,
      prioritySupport: true,
    },
    featureList: [
      { text: 'All Pro features', included: true },
      { text: 'Unlimited habits, tasks & goals', included: true },
      { text: 'Google Calendar sync', included: true },
      { text: 'Custom color themes', included: true },
      { text: 'Gifted by the mind. team 💜', included: true },
    ],
  },
  dev: {
    id: 'dev',
    name: 'Developer',
    description: 'Admin access for development',
    monthlyPrice: 0,
    yearlyPrice: 0,
    limits: {
      maxHabits: Infinity,
      maxGoals: Infinity,
      maxTasks: Infinity,
      maxEvents: Infinity,
    },
    features: {
      habits: true,
      basicStats: true,
      streaks: true,
      tasks: true,
      goals: true,
      milestones: true,
      events: true,
      googleCalendar: true,
      analytics: true,
      themes: true,
      exportData: true,
      recurringTasks: true,
      prioritySupport: true,
      // Dev-only features
      adminPanel: true,
      debugMode: true,
      bypassPaywall: true,
      impersonateUser: true,
      viewAllUsers: true,
    },
    featureList: [
      { text: 'All Pro features', included: true },
      { text: 'Admin panel access', included: true },
      { text: 'Debug mode', included: true },
      { text: 'User management', included: true },
    ],
  },
};

/**
 * Check if a user has access to a specific feature
 * @param {string} userTier - The user's current tier
 * @param {string} feature - The feature key to check
 * @returns {boolean} - Whether the user has access
 */
export const getFeatureAccess = (userTier, feature) => {
  const tier = TIERS[userTier];
  if (!tier) return TIERS.free.features[feature] ?? false;
  return tier.features[feature] ?? false;
};

/**
 * Get the limit for a specific item type
 * @param {string} userTier - The user's current tier
 * @param {string} limitName - The limit key (maxHabits, maxGoals, etc.)
 * @returns {number} - The limit value
 */
export const getLimit = (userTier, limitName) => {
  const tier = TIERS[userTier];
  if (!tier) return TIERS.free.limits[limitName] ?? 0;
  return tier.limits[limitName] ?? TIERS.free.limits[limitName];
};

/**
 * Check if a UID is in the dev list
 * @param {string} uid - Firebase user UID
 * @returns {boolean} - Whether the user is a dev
 */
export const isDevUser = (uid) => {
  return DEV_UIDS.includes(uid);
};

/**
 * Get tier config for display
 * @param {string} tierId - The tier ID
 * @returns {object} - The tier configuration
 */
export const getTierConfig = (tierId) => {
  return TIERS[tierId] || TIERS.free;
};

/**
 * Get all tiers as array for public display (excludes friend and dev tiers)
 * Friend tier is a secret tier only granted via admin panel
 * @param {boolean} includeDev - Whether to include dev tier
 * @returns {array} - Array of tier configs
 */
export const getAllTiers = (includeDev = false) => {
  const tiers = [TIERS.free, TIERS.pro];
  if (includeDev) tiers.push(TIERS.dev);
  return tiers;
};

/**
 * Get all tier IDs for admin management
 * @returns {array} - Array of tier IDs
 */
export const getAllTierIds = () => {
  return ['free', 'pro', 'friend', 'dev'];
};

/**
 * Check if tier has full/unlimited access (pro, friend, or dev)
 * @param {string} tierId - The tier ID
 * @returns {boolean} - Whether tier has full access
 */
export const hasFullAccess = (tierId) => {
  return ['pro', 'friend', 'dev'].includes(tierId);
};
